import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { closeCallNotifications } from '@/lib/callNotifications';
import { RecordingReceiver, type RecSignal } from '@/lib/recordingTransfer';
import { ChatMirrorClient } from '@/lib/companionChatMirror';
import { chatTransport } from '@/lib/chatTransport';
import { useChatStore } from '@/state/chatStore';
import { useUIStore } from '@/state/store';

/**
 * Welle 12 (pre118): Companion-Remote (Variante A).
 * Der Companion verbindet sich als nicht-präsenter „Remote" mit /agent (companion:true),
 * empfängt die EINGEHENDE Call-Signalisierung identisch zur A/V-Sitzung und spiegelt deren
 * A/V-Status. Buttons senden `remote:cmd` → das Backend reicht sie an die A/V-Sitzung weiter,
 * die die Aktion (annehmen/ablehnen/auflegen/mute/kamera) lokal ausführt.
 */
export interface RemoteAvState { inCall: boolean; micMuted?: boolean; camOn?: boolean; guestConnected?: boolean; recording?: boolean; recPending?: boolean; recTopic?: string }
export interface RemoteIncoming { callId: string; steleName?: string; tenantName?: string; group?: string; strategy?: string; waitingMs?: number }

export function useRemoteControl() {
  const socketRef = useRef<Socket | null>(null);
  const recRef = useRef<RecordingReceiver | null>(null);
  const [connected, setConnected] = useState(false);
  const [incoming, setIncoming] = useState<RemoteIncoming | null>(null);
  const [avState, setAvState] = useState<RemoteAvState>({ inCall: false });

  useEffect(() => {
    const token = localStorage.getItem('bamoa_token');
    if (!token) return;
    const socket = io('/agent', { auth: { token, companion: true }, transports: ['websocket'], reconnection: true });
    socketRef.current = socket;

    // pre190: Companion-Chat-Spiegel (Client). Offer an die A/V-Session; Updates → chatStore.
    const chatMirror = new ChatMirrorClient(
      (sig) => { try { socket.emit('remote:cmd', { action: '__chat', chat: sig }); } catch { /* */ } },
      (m) => {
        if (m?.t === 'msg') useChatStore.getState().addMessage(m.peerId, m.name ?? 'Berater', m.msg);
        else if (m?.t === 'status') useChatStore.getState().setStatus(m.peerId, m.st);
        else if (m?.t === 'snapshot') useChatStore.setState({ threads: m.threads ?? {}, names: m.names ?? {}, status: m.status ?? {} });
        else if (m?.t === 'read') useChatStore.getState().markRead(m.peerId);
      },
    );
    let mirrorRetry: ReturnType<typeof setInterval> | null = setInterval(() => { if (!chatMirror.isOpen) void chatMirror.start(); }, 4000);
    chatMirror.onOpen = () => { if (mirrorRetry) { clearInterval(mirrorRetry); mirrorRetry = null; } };
    chatMirror.onClose = () => { if (!mirrorRetry) mirrorRetry = setInterval(() => { if (!chatMirror.isOpen) void chatMirror.start(); }, 4000); };
    chatTransport.mirrorSend = (obj) => chatMirror.send(obj);

    socket.on('connect', () => { setConnected(true); void chatMirror.start(); });
    socket.on('disconnect', () => setConnected(false));
    let myUserId: string | null = null;
    socket.on('hello', (data: any) => {
      setConnected(true);
      myUserId = data?.userId ?? myUserId;
      // pre195: Anfangsstatus direkt vom Server übernehmen (sonst zeigt die Leiste bis zum
      // ersten agent:status-Broadcast den Default 'Verfügbar').
      const st = useUIStore.getState();
      if (data?.phoneBusy) { st.setStatus('phone'); st.setPhoneBusy(true); }
      else if (data?.status === 'pause') st.setStatus('pause');
      else if (data?.status === 'wrap') st.setStatus('wrap');
      else if (data?.status === 'available') { st.setStatus('available'); st.setPhoneBusy(false); }
    });

    // pre194: Status-Leiste wie in der Berateransicht. Pillen-Klicks (Window-Events des
    // StatusSwitchers) an den Server, und den serverseitigen Status (agent:status) spiegeln.
    const onMyStatus = (e: Event) => { const st = (e as CustomEvent).detail?.status; if (st) socket.emit('status:set', { status: st }); };
    const onMyPhoneBusy = (e: Event) => { socket.emit('phone:busy', { busy: (e as CustomEvent).detail?.busy === true }); };
    window.addEventListener('bamoa:my-status', onMyStatus);
    window.addEventListener('bamoa:phone-busy', onMyPhoneBusy);
    socket.on('agent:status', (d: { userId?: string; status?: string }) => {
      if (!d?.userId || d.userId !== myUserId) return;
      const st = useUIStore.getState();
      if (d.status === 'available') { st.setStatus('available'); st.setPhoneBusy(false); }
      else if (d.status === 'pause') st.setStatus('pause');
      else if (d.status === 'wrap') st.setStatus('wrap');
      else if (d.status === 'phone') { st.setStatus('phone'); st.setPhoneBusy(true); }
    });
    socket.on('call:incoming', (d: RemoteIncoming) => setIncoming(d));
    socket.on('call:cancelled', (d: any) => { setIncoming(null); closeCallNotifications(d?.callId); window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: d })); });
    socket.on('call:timeout', (d: any) => { setIncoming(null); closeCallNotifications(d?.callId); window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: d })); });
    socket.on('call:accepted', (d: any) => { setIncoming(null); closeCallNotifications(d?.callId); window.dispatchEvent(new CustomEvent('bamoa:call-cleared', { detail: d })); });
    // Welle 12 (pre126): Statusleiste im Companion — Klingeln & Chat-Nachrichten brücken.
    socket.on('call:ringing', (d) => window.dispatchEvent(new CustomEvent('bamoa:call-ringing', { detail: d })));
    socket.on('chat:message', (m) => window.dispatchEvent(new CustomEvent('bamoa:chat-message', { detail: m })));
    // Welle 12 (pre121): Experten-Beitritt (Magic-Link) auch im Companion anzeigen.
    // Das Zulassen ist ein reiner Backend-Gate-Call (gleicher Nutzer) → JoinRequestPrompt
    // ruft ihn direkt; die A/V-Sitzung nimmt den Experten danach per Signaling in den Mesh.
    socket.on('join:request', (d) => window.dispatchEvent(new CustomEvent('bamoa:join-request', { detail: d })));
    socket.on('join:resolved', (d) => window.dispatchEvent(new CustomEvent('bamoa:join-resolved', { detail: d })));
    // pre140: Telefonie-Status spiegeln (Toggle wurde hier oder in der A/V-Sitzung umgelegt).
    socket.on('agent:phone-status', (d: { userId: string; busy: boolean }) => {
      useUIStore.getState().setPhoneBusy(d?.busy === true);
    });
    socket.on('remote:state', (s: any) => {
      // pre190: Companion-Chat-Spiegel — Answer/ICE der A/V-Session.
      if (s && s.__chat) { void chatMirror.onSignal(s.__chat); return; }
      // Welle 12 (pre134): Aufzeichnungspaket-Übergabe — Signaling huckepack auf remote:state.
      if (s && s.__rec) {
        const sig = s.__rec as RecSignal;
        if (sig.type === 'offer') {
          try { recRef.current?.cleanup(); } catch { /* */ }
          const receiver = new RecordingReceiver(
            (out) => socket.emit('remote:cmd', { action: '__rec', rec: out }),
            (blob, meta) => window.dispatchEvent(new CustomEvent('bamoa:rec-received', { detail: { blob, ...meta } })),
            (pct) => window.dispatchEvent(new CustomEvent('bamoa:rec-progress', { detail: { pct } })),
          );
          recRef.current = receiver;
          void receiver.onSignal(sig);
        } else {
          void recRef.current?.onSignal(sig);
        }
        return;
      }
      setAvState(s ?? { inCall: false });
      if (s?.inCall) setIncoming(null);
    });
    return () => {
      if (mirrorRetry) clearInterval(mirrorRetry);
      window.removeEventListener('bamoa:my-status', onMyStatus);
      window.removeEventListener('bamoa:phone-busy', onMyPhoneBusy);
      try { chatMirror.close(); } catch { /* */ }
      chatTransport.mirrorSend = null;
      try { recRef.current?.cleanup(); } catch { /* */ }
      try { socket.disconnect(); } catch { /* */ }
      socketRef.current = null;
    };
  }, []);

  const sendCmd = useCallback((action: string, extra?: Record<string, unknown>) => {
    socketRef.current?.emit('remote:cmd', { action, ...(extra ?? {}) });
  }, []);

  return {
    connected,
    incoming,
    avState,
    accept: () => { sendCmd('accept'); setIncoming(null); },
    decline: () => { sendCmd('decline'); setIncoming(null); },
    hangup: () => sendCmd('hangup'),
    toggleMic: () => sendCmd('mute'),
    toggleCam: () => sendCmd('camera'),
    swap: () => sendCmd('camera-swap'),
    kickGuest: () => sendCmd('kick-guest'),
    startRecording: (topic: string) => sendCmd('rec-start', { topic }),
    stopRecording: () => sendCmd('rec-stop'),
    // pre140: Telefonie-Toggle direkt über den eigenen Companion-Socket (companion:true);
    // der Gateway-Handler liest die userId und nimmt den Berater aus dem Video-Routing.
    setPhoneBusy: (busy: boolean) => { socketRef.current?.emit('phone:busy', { busy }); },
  };
}
